import { Button } from '@helvetia/components/Button'
import { OptionSelectorField } from '@helvetia/components/forms/OptionSelector'
import { Col, Row } from '@helvetia/components/layout'
import { MatchMedia } from '@helvetia/components/MatchMedia'
import Spinner from '@helvetia/components/Spinner'
import Text from '@helvetia/components/Text'
import dayjs from 'dayjs'
import { Field, Form, Formik } from 'formik'
import * as React from 'react'
import { WithNamespaces, withNamespaces } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { getDefaultSelectedLanguage } from '../../../core/services/storage.service'
import { TitleWrapper } from '../../_components/common.styles'
import { LifecycleView } from '../../_components/ConnectComponent'
import DatePicker from '../../_components/DatePicker'
import { DropDownSelect } from '../../_components/DropDownSelect'
import ContactBox from '../../_components/HelvetiaContactBox'
import { OFFER_MONTHS_VALIDITY } from '../../_utils/environmentConst.provider'
import { ContractStepper } from '../ContractStepper'
import { DateDurationPresenter } from './DateDuration.presenter'
import { validateFieldForm } from './DateDuration.validators'

export interface View extends LifecycleView {
  showLoading: () => void
  hideLoading: () => void
}

export interface DateDurationInfoValues {
  validityStartDate: string
  validityInYears: number
}

interface Props {
  presenter: DateDurationPresenter
  onSubmit: (values: DateDurationInfoValues) => void
}

interface State {
  initialValues: DateDurationInfoValues
  isLoading: boolean
}

export class DateDurationSection
  extends React.Component<Props & RouteComponentProps & WithNamespaces, State>
  implements View {
  private presenter: DateDurationPresenter
  private durationYears

  constructor(props) {
    super(props)

    this.presenter = props.presenter
    this.presenter.initialize(this)

    this.durationYears = [
      { text: '1', value: 1 },
      { text: '3', value: 3 },
      { text: '5', value: 5 },
    ]

    this.state = {
      initialValues: null,
      isLoading: false,
    }
  }

  setInitialValues = (initialValues: DateDurationInfoValues) => {
    this.setState({
      initialValues,
    })
  }

  showLoading = () => this.setState({ isLoading: true })

  hideLoading = () => this.setState({ isLoading: false })

  handleClickContinuar = (values: DateDurationInfoValues) =>
    this.presenter.onContinue(values)

  handleClickBack = () => this.presenter.onBackClick()

  render() {
    const { t } = this.props
    const lang = getDefaultSelectedLanguage()

    const offerLimitDate = dayjs(new Date()).add(OFFER_MONTHS_VALIDITY, 'month')

    if (!this.state.initialValues) return <Spinner size="xl" />
    return (
      <>
        <Row>
          <Col mobile={12} tablet={12} desktop={4} verticalGutter>
            <ContractStepper step={1} />
          </Col>
          <Col tablet={12} desktop={8}>
            <Row>
              <TitleWrapper mobile={12} tablet={12}>
                <Text as="p" color="black" size="l">
                  {t('contractStep1.title')}
                </Text>
              </TitleWrapper>
            </Row>

            <Formik
              initialValues={this.state.initialValues}
              onSubmit={this.handleClickContinuar}
              validate={values => validateFieldForm(values)}
              render={({ values }) => (
                <Form>
                  <Row marginTop="xs">
                    <Col>
                      <Field
                        component={DatePicker}
                        name="validityStartDate"
                        text={t('contractStep1.validityStartDate.question')}
                        label={t('contractStep1.validityStartDate.label')}
                        type="text"
                        maxDate={[
                          offerLimitDate.year(),
                          offerLimitDate.month() + 1,
                          offerLimitDate.date(),
                        ]}
                        lang={lang}
                      />
                    </Col>
                  </Row>
                  <Row marginTop="s" marginBottom="m">
                    <Col>
                      <Text as="div" size="s">
                        {t('contractStep1.dateInformation')}
                      </Text>
                    </Col>
                  </Row>
                  <Row marginTop="xs" marginBottom="s">
                    <Col>
                      <MatchMedia onlyMobile>
                        <Field
                          component={DropDownSelect}
                          defaultSelected={values.validityInYears}
                          name="validityInYears"
                          question={t('contractStep1.validityInYears.question')}
                          label={t('contractStep1.validityInYears.label')}
                          options={this.durationYears}
                          required
                        />
                      </MatchMedia>
                      <MatchMedia tablet>
                        <Field
                          component={OptionSelectorField}
                          name="validityInYears"
                          textLabel={t(
                            'contractStep1.validityInYears.question',
                          )}
                          label={t('contractStep1.validityInYears.label')}
                          optionList={this.durationYears}
                          required
                        />
                      </MatchMedia>
                    </Col>
                  </Row>

                  <Row verticalGutter marginTop="s" marginBottom="m">
                    <Col verticalGutter mobile={12} tablet={3} desktop={3}>
                      <Button primary type="submit">
                        {t('buttons.continue')}
                      </Button>
                    </Col>
                    <Col verticalGutter mobile={12} tablet={3} desktop={3}>
                      <Button grey onClick={this.handleClickBack}>
                        {t('buttons.back')}
                      </Button>
                    </Col>
                  </Row>
                  <Row verticalGutter>
                    <Col>
                      <ContactBox />
                    </Col>
                  </Row>
                </Form>
              )}
            />
          </Col>
        </Row>
      </>
    )
  }
}

export const DateDurationView = withNamespaces()(
  withRouter(DateDurationSection),
)
